import { inject, Injectable } from '@angular/core';
import { CurrentUserResponse } from './models/user.model';
import { APP_CONFIG, AppConfig } from '@cfg/env/app.config';
import { AppHttpClientApi } from '@helpers/api/http-client-api';
import { catchError, map, Observable, shareReplay, throwError } from 'rxjs';
import { AppError } from '@domain-utils/app-error';
import { ApiException } from '@helpers/errors/api-error';
import { AppErrorItem } from '@domain-utils/app-error-item';
import { SERVICE_ERROR_CONSTANTS } from './constants/service-error.const';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly http = inject(AppHttpClientApi);
  private readonly appConfig: AppConfig = inject(APP_CONFIG);
  private readonly userResource = 'cargamasivaemp/user/current';

  private userCache$: Observable<CurrentUserResponse> | null = null;

  /**
   * Obtiene la información del usuario actual ejecutando un GET sencillo contra el endpoint indicado.
   */
  getUser(): Observable<CurrentUserResponse> {
    if (this.appConfig.useMocks) {
      return new Observable((observer) => {
        observer.next({ username: 'Misko Hevery Mock', dni: '123' });
        observer.complete();
      });
    }

    this.userCache$ ??= this.http
      .getPlain<CurrentUserResponse>(this.userResource)
      .pipe(
        shareReplay(1),
        map((response) => {
          if (!response) {
            throw new AppError(SERVICE_ERROR_CONSTANTS.USER.FETCH_FAILED, [
              SERVICE_ERROR_CONSTANTS.USER.RESPONSE_EMPTY
            ]);
          }

          const username = `${response.username ?? ''}`.trim();
          if (!username) {
            throw new AppError(SERVICE_ERROR_CONSTANTS.USER.FETCH_FAILED, [
              SERVICE_ERROR_CONSTANTS.USER.USERNAME_MISSING
            ]);
          }

          return {
            ...response,
            username
          } as CurrentUserResponse;
        }),
        catchError((error: unknown) => {
          if (error instanceof AppError) {
            return throwError(() => error);
          }
          if (error instanceof ApiException) {
            const codes: AppErrorItem[] = error.error.map((item) => ({
              key: item.key,
              message: item.message
            }));

            return throwError(
              () =>
                new AppError(SERVICE_ERROR_CONSTANTS.USER.FETCH_FAILED, codes)
            );
          }
          return throwError(
            () =>
              new AppError(SERVICE_ERROR_CONSTANTS.USER.FETCH_FAILED, [
                {
                  key: SERVICE_ERROR_CONSTANTS.USER.UNKNOWN_ERROR.key,
                  message:
                    error instanceof Error
                      ? error.message
                      : SERVICE_ERROR_CONSTANTS.USER.UNKNOWN_ERROR.message
                }
              ])
          );
        })
      );
    return this.userCache$;
  }
}
